/**
 * Shared types and utilities for StashThumbnail layout components.
 *
 * Any component that renders a collection of StashThumbnails with selection
 * support (Grid, Masonry, List, etc.) should use ThumbnailSelectProps for its
 * interface and buildThumbnailItemProps to derive per-item props.
 *
 * This ensures all layouts produce identical interaction behavior —
 * navigation, edit-mode toggling, overlays — from the same inputs.
 */

import type { ReactNode } from "react";
import type { StashThumbnailData } from "./StashThumbnail";

/**
 * Shared props accepted by any layout that renders selectable StashThumbnails.
 *
 * When `onToggleSelect` is provided, thumbnails enter edit/tag mode:
 * clicks invoke the handler instead of navigating.
 * When `undefined`, thumbnails link to their detail page.
 */
export interface ThumbnailSelectProps {
  isEditMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (postId: string) => void;
  unavailableIds?: ReadonlySet<string>;
  onRetryItem?: (post: StashThumbnailData) => unknown;
  getItemFooter?: (post: StashThumbnailData) => ReactNode;
  getItemOverlay?: (post: StashThumbnailData) => ReactNode;
  getItemClassName?: (post: StashThumbnailData) => string | undefined;
  /** When set, appends ?collection=ID(&invite=CODE) to stash links */
  collectionContext?: { collectionId: string; inviteCode?: string };
}

/**
 * Derives per-item StashThumbnail props from collection-level config.
 *
 * Single source of truth for the mapping:
 *   collection select props + post → individual thumbnail props
 *
 * Keeps Grid, Masonry, and any future layout in sync so they can't
 * diverge on how selection, navigation, or overlays are handled.
 */
export function buildThumbnailItemProps(
  post: StashThumbnailData,
  config: ThumbnailSelectProps,
) {
  let href: string | undefined;
  if (!config.onToggleSelect) {
    const base = `/stash/${post.id}`;
    if (config.collectionContext) {
      const params = new URLSearchParams({ collection: config.collectionContext.collectionId });
      if (config.collectionContext.inviteCode) {
        params.set("invite", config.collectionContext.inviteCode);
      }
      href = `${base}?${params.toString()}`;
    } else {
      href = base;
    }
  }

  return {
    href,
    isEditMode: config.isEditMode ?? false,
    isSelected: config.selectedIds?.has(post.id) ?? false,
    onToggleSelect: config.onToggleSelect,
    overlay: config.getItemOverlay?.(post),
    footer: config.getItemFooter?.(post),
    unavailable: config.unavailableIds?.has(post.id),
    onRetry: config.onRetryItem ? () => config.onRetryItem?.(post) : undefined,
    className: config.getItemClassName?.(post),
  };
}
